import NextImage from 'next/image'
import { useCallback, useContext, useMemo } from 'react'

import { SanityFrameBuilderProfile } from '@frame-builder/data/sanity/queries/types/profile'
import { type SanityFrameBuilderTexture } from '@frame-builder/data/sanity/queries/types/texture'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import { getItemTitle, toRecord } from '@frame-builder/lib/helpers'
import { type ItemPropsWithId } from '@frame-builder/lib/item'
import {
  getBasicImageLoader,
  getSanityImageUrl,
} from '@frame-builder/lib/image'
import {
  getStatePrice,
  getStatePriceWithDiscount,
} from '@frame-builder/lib/state-price'
import { getIsTextureValid } from '@frame-builder/lib/validation/state-validation'

import ItemImage from '@frame-builder/components/utility/item-image'
import ItemList from '@frame-builder/components/utility/item-list'
import PriceBadge from '@frame-builder/components/utility/price-badge'
import ProfileDetails from './profile-details'

const ProfileList = () => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { lastValidState, enabledItemMap } = useContext(
    FrameBuilderValidationContext,
  )

  const profileTextureMap = useMemo(() => {
    const selectedProfile = selectedState.profile ?? lastValidState.profile
    const selectedTexture = selectedState.texture

    return toRecord<SanityFrameBuilderTexture | undefined>(
      frameBuilderConfiguration.profiles.map((profile) => {
        const defaultTexture = frameBuilderConfiguration.textures.find(
          (texture) => texture._id === profile.defaultTexture?._id,
        )
        const isTextureValid = getIsTextureValid(
          frameBuilderConfiguration,
          profile,
          selectedTexture,
        )

        return {
          [profile._id]:
            profile._id === selectedProfile?._id ||
            isTextureValid ||
            !defaultTexture
              ? selectedTexture
              : defaultTexture,
        }
      }),
    )
  }, [frameBuilderConfiguration, lastValidState.profile, selectedState])

  // Calculate frame prices for comparison
  const framePrices = useMemo(() => {
    return toRecord<number>(
      frameBuilderConfiguration.profiles.map((profile) => {
        const price = getStatePrice(
          frameBuilderType,
          frameBuilderConfiguration,
          {
            ...committedState,
            profile,
            texture: profileTextureMap[profile._id],
          },
        )

        return {
          [profile._id]: price,
        }
      }),
    )
  }, [
    committedState,
    frameBuilderConfiguration,
    frameBuilderType,
    profileTextureMap,
  ])

  const framePricesWithDiscount = useMemo(() => {
    return toRecord<number>(
      frameBuilderConfiguration.profiles.map((profile) => {
        const price = getStatePriceWithDiscount(
          frameBuilderType,
          frameBuilderConfiguration,
          {
            ...committedState,
            profile,
            texture: profileTextureMap[profile._id],
          },
        )

        return {
          [profile._id]: price,
        }
      }),
    )
  }, [
    committedState,
    frameBuilderConfiguration,
    frameBuilderType,
    profileTextureMap,
  ])

  const selectProfile = useCallback(
    (profile: SanityFrameBuilderProfile) => {
      dispatchSelectedState({
        type: 'frameStyle',
        profile,
      })
    },
    [dispatchSelectedState],
  )

  const handleChange = useCallback(
    (id: string) => {
      const newProfile = frameBuilderConfiguration.profiles.find(
        (profile) => profile._id === id,
      )

      if (newProfile) {
        selectProfile(newProfile)
      }
    },
    [frameBuilderConfiguration, selectProfile],
  )

  return (
    <ItemList
      defaultValue={selectedState.profile?._id}
      onChange={handleChange}
      items={frameBuilderConfiguration.profiles.map((profile) => {
        const texture = profileTextureMap[profile._id]
        const textureImage = texture?.icon ?? texture?.image
        const textureImageUrl = getSanityImageUrl(textureImage, {
          width: 24,
          height: 24,
        })
        const profileTexture = frameBuilderConfiguration.profileTextures.find(
          (profileTexture) => {
            return (
              texture &&
              profileTexture.profile?._ref === profile._id &&
              profileTexture.texture?._ref === texture._id
            )
          },
        )
        const isEnabled = enabledItemMap.profileIds.includes(profile._id)
        const title = getItemTitle(
          frameBuilderStrings,
          profile.title,
          isEnabled ? profileTexture?.discountPercent : 0,
        )

        const imageElement = <ItemImage image={profile.icon} alt={title} />
        const subtitleElement = <>{profile.subtitle}</>
        let descriptionElement: JSX.Element | undefined
        const detailsElement = <ProfileDetails profile={profile} />
        let badgeElement: JSX.Element | undefined

        if (
          selectedState.profile &&
          selectedState.profile._id !== profile._id &&
          isEnabled
        ) {
          badgeElement = (
            <>
              <PriceBadge
                price={
                  framePricesWithDiscount[profile._id] -
                  framePricesWithDiscount[selectedState.profile._id]
                }
                showPlusSign
              />
              {!!profileTexture?.discountPercent && (
                <PriceBadge
                  price={
                    framePricesWithDiscount[profile._id] -
                    framePrices[profile._id]
                  }
                  showPlusSign
                  disabled
                />
              )}
            </>
          )
        }

        if (!!texture && !!textureImage && !!textureImageUrl) {
          descriptionElement = (
            <NextImage
              src={textureImageUrl}
              width={textureImage.asset?.metadata?.dimensions?.width}
              height={textureImage.asset?.metadata?.dimensions?.height}
              alt={texture.title ?? ''}
              loader={getBasicImageLoader}
              unoptimized
              className="w-6 h-6"
            />
          )
        }

        const item: ItemPropsWithId = {
          id: profile._id,
          title,
          imageElement,
          subtitleElement,
          descriptionElement,
          detailsElement,
          badgeElement,
          disabled: !isEnabled,
          error: lastValidState.profile?._id === profile._id,
        }

        return item
      })}
    />
  )
}

export default ProfileList
